import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface ModalData {
  id: string
  component?: any
  props?: Record<string, any>
  title?: string
  content?: string
  showCloseButton?: boolean
  persistent?: boolean
  onClose?: () => void // Callback при закрытии модалки
  isClosing?: boolean // Флаг для анимации закрытия
}

export const useModalStore = defineStore('modal', () => {
  const modals = ref<ModalData[]>([])

  const openModal = (modal: Omit<ModalData, 'id'>) => {
    const id = `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const modalData: ModalData = {
      id,
      showCloseButton: true,
      persistent: false,
      isClosing: false,
      ...modal
    }
    modals.value.push(modalData)
    return id
  }

  const closeModal = (id: string) => {
    const index = modals.value.findIndex(modal => modal.id === id)
    if (index !== -1) {
      const modal = modals.value[index]
      
      // Устанавливаем флаг закрытия для запуска анимации
      modal.isClosing = true
      
      // Удаляем модал после анимации (300ms)
      setTimeout(() => {
        const currentIndex = modals.value.findIndex(m => m.id === id)
        if (currentIndex !== -1) {
          const modalToRemove = modals.value[currentIndex]
          // Вызываем callback при закрытии, если он есть
          if (modalToRemove.onClose) {
            modalToRemove.onClose()
          }
          modals.value.splice(currentIndex, 1)
        }
      }, 300)
    }
  }

  const closeLastModal = () => {
    if (modals.value.length > 0) {
      const modal = modals.value[modals.value.length - 1]
      
      // Устанавливаем флаг закрытия для запуска анимации
      modal.isClosing = true
      
      // Удаляем модал после анимации (300ms)
      setTimeout(() => {
        if (modals.value.length > 0) {
          const modalToRemove = modals.value[modals.value.length - 1]
          // Вызываем callback при закрытии, если он есть
          if (modalToRemove.onClose) {
            modalToRemove.onClose()
          }
          modals.value.pop()
        }
      }, 300)
    }
  }

  const closeAllModals = () => {
    // Устанавливаем флаг закрытия для всех модалов
    modals.value.forEach(modal => {
      modal.isClosing = true
    })
    
    // Удаляем все модалы после анимации (300ms)
    setTimeout(() => {
      // Вызываем callbacks для всех модалок
      modals.value.forEach(modal => {
        if (modal.onClose) {
          modal.onClose()
        }
      })
      modals.value.length = 0
    }, 300)
  }

  const getModal = (id: string) => {
    return modals.value.find(modal => modal.id === id)
  }

  return {
    modals,
    openModal,
    closeModal,
    closeLastModal,
    closeAllModals,
    getModal
  }
})
